import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useMutationAddMemberToOccurrenceAsMember,
  useMutationBookRsvpAsMember,
  useMutationCancelRsvpAsMember,
  useMutationLateCancelAsMember,
  useQueryRulesAsPublic,
  useQuerySessionPackUsageAsMember,
} from "@gymflow/api";
import { formatCurrency, ordinal, pluralize } from "@gymflow/helpers";
import { EventRsvpStatus } from "@gymflow/types";
import {
  evaluateBookingWindow,
  isOccurrenceInsideCancelationWindow,
} from "@gymflow/validation";
import { useQuery } from "@tanstack/react-query";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { RouteFeature } from "apps/portal/src/routes";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { DateTime, Interval } from "luxon";
import React, { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import useGymflowModels from "../../../store";
import {
  Button,
  CalendarIcon,
  ClockIcon,
  CreditsIcon,
  LayeredCircles,
  ShopBagIcon,
  Spinner,
} from "../../atoms";
import { DotBadge } from "../../atoms/DotBadge";
import { ExclamationCircleIcon } from "../../atoms/icons/ExclamationCircleIcon";
import { LocationMarkerIcon } from "../../atoms/icons/LocationMarkerIcon";
import { StaffAvatar } from "../../molecules/StaffAvatar";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import HostedPagesQuickPurchaseOverlay from "../HostedPagesQuickPurchase";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import { getTimetableQueryParams } from "./components/useTimetableQueryParamsStore";

export type HostedPagesClassOverviewProps = {};

export const HostedPagesClassOverview: React.FC<
  HostedPagesClassOverviewProps
> = ({}) => {
  const { eventOccurrenceId, viewType } = useParams<{
    eventOccurrenceId: string;
    viewType: "calendar" | "list";
  }>();
  const history = useHistory();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const { userId: id } = useContext(AuthenticationContext);

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      `${RouteFeature.SiteTimetableType.replace(
        ":viewType",
        viewType,
      )}?${getTimetableQueryParams()}`,
    ),
  );
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { club } = useClubSettings();

  const { data: eventOccurrence, isLoading } = useQuery(
    apiV2.member.eventOccurrence.queries.id(parseInt(eventOccurrenceId)),
  );

  const { data: sessionPackUsage } = useQuerySessionPackUsageAsMember({
    api,
    params: eventOccurrence
      ? {
          sessionCost: eventOccurrence.sessionCost,
          activityCategoryId: eventOccurrence.activityCategoryId,
        }
      : undefined,
  });

  const { mutateAsync: mutateAsyncAddMemberToOccurrence } =
    useMutationAddMemberToOccurrenceAsMember({ api });

  const { mutateAsync: mutateAsyncBookRsvp } = useMutationBookRsvpAsMember({
    api,
  });

  const { mutateAsync: mutateAsyncCancelRsvp } = useMutationCancelRsvpAsMember({
    api,
  });

  const { mutateAsync: mutateAsyncLateCancel } = useMutationLateCancelAsMember({
    api,
  });

  const slotsAvailable =
    (eventOccurrence?.capacity ?? 0) - (eventOccurrence?.bookedCount ?? 0);
  const waitlistSlotsAvailable =
    (eventOccurrence?.waitlistCapacity ?? 0) -
    (eventOccurrence?.waitingCount ?? 0);
  const bookActionType =
    slotsAvailable > 0
      ? "BOOK"
      : waitlistSlotsAvailable
        ? "JOIN_WAITLIST"
        : "CLASS_FULL";
  const { data: activityRules } = useQueryRulesAsPublic({
    api,
    params: eventOccurrence
      ? {
          activityId: eventOccurrence?.activityId,
        }
      : undefined,
  });
  const bookingCancelationRule = activityRules?.ruleClubList.find(
    (e) => e.bookingCancellationRule,
  );

  const isEventInsideBookingWindow =
    eventOccurrence?.startAt && activityRules
      ? evaluateBookingWindow(eventOccurrence.startAt, activityRules).canBook
      : undefined;

  const isLeaveWaitlistAvailable = eventOccurrence?.rsvpStatus === "WAITING";

  const isInsideCancelationWindow = isOccurrenceInsideCancelationWindow(
    eventOccurrence?.startAt,
    bookingCancelationRule,
  );

  const isCancelWithoutFee =
    isInsideCancelationWindow || isLeaveWaitlistAvailable;

  const lateCancelationFee =
    bookingCancelationRule?.bookingCancellationRule?.cancellationFee;

  const closeOverlay = useCallback(() => {
    if (!eventOccurrence) {
      return;
    }
    history.replace(
      createSiteOrEmbedLink(
        RouteFeature.SiteTimetableId.replace(":viewType", viewType).replace(
          ":eventOccurrenceId",
          eventOccurrenceId,
        ),
      ),
    );
  }, [
    createSiteOrEmbedLink,
    eventOccurrence,
    eventOccurrenceId,
    history,
    viewType,
  ]);
  const { toast } = useContext(ToastContext);
  const [parent] = useAutoAnimate();
  const [innerParent] = useAutoAnimate();

  return (
    <div className="track-height flex max-h-fit w-full flex-col overflow-y-auto">
      <HostedPagesBackHeader
        link={
          history.location.key
            ? undefined
            : createSiteOrEmbedLink(
                `${RouteFeature.SiteTimetableType.replace(
                  ":viewType",
                  viewType,
                )}?${getTimetableQueryParams()}`,
              )
        }
        label={
          !eventOccurrence?.rsvpStatus ||
          eventOccurrence?.rsvpStatus === "LATE_CANCELLED" ||
          eventOccurrence?.rsvpStatus === "CANCELLED"
            ? t("pages.hostedPagesClassOverView.confirmBooking")
            : t("pages.hostedPagesClassOverView.cancelBooking")
        }
      />
      <div ref={parent} className="flex h-full flex-col items-center">
        {isLoading && <Spinner />}
        {!isLoading && eventOccurrence && (
          <div
            ref={innerParent}
            className="flex h-fit w-[32rem] max-w-full flex-col items-center gap-8 overflow-y-auto overflow-x-hidden p-6 max-lg:justify-between "
          >
            <div className="flex w-full flex-col items-center gap-6">
              <div className="flex w-full flex-col gap-1">
                <div className="wrap flex flex-row items-center justify-between gap-2">
                  <div className="dark:text-gray-0 flex w-full text-xl font-bold text-gray-950">
                    {eventOccurrence.activityName}
                  </div>
                  <div className="flex flex-row items-center gap-1">
                    <ClockIcon
                      pathClassName="stroke-gray-500 stroke-1"
                      className="size-5"
                    />
                    <div className="dark:text-gray-0 font-semibold text-gray-950">
                      {Interval.fromDateTimes(
                        DateTime.fromISO(eventOccurrence.startAt),
                        DateTime.fromISO(eventOccurrence.endAt),
                      )
                        .toDuration()
                        .rescale()
                        .toHuman({
                          unitDisplay: "narrow",
                          listStyle: "short",
                        })}
                    </div>
                  </div>
                </div>
                {eventOccurrence.activityDescription && (
                  <div className="flex w-full text-sm text-gray-600 dark:text-gray-400">
                    {eventOccurrence.activityDescription}
                  </div>
                )}
              </div>
              {eventOccurrence?.rsvpStatus && (
                <div className="flex w-full">
                  <DotBadge
                    intent={
                      (
                        {
                          WAITING: "warning",
                          BOOKED: "lightblue",
                          ATTENDED: "success",
                          CANCELLED: "warning",
                          LATE_CANCELLED: "error",
                          NO_SHOW: "error",
                        } as const
                      )[eventOccurrence?.rsvpStatus]
                    }
                  >
                    {eventOccurrence?.rsvpStatus === "WAITING"
                      ? t("portal.common.positionOnWaitlist", {
                          position: ordinal(
                            eventOccurrence?.waitlistPosition ?? 0,
                          ),
                        })
                      : t(
                          `portal.common.rsvpStatus.${eventOccurrence?.rsvpStatus}`,
                        )}
                  </DotBadge>
                </div>
              )}
              <div className="flex w-full flex-col gap-1">
                <StaffAvatar
                  userStaff={{
                    firstName: eventOccurrence.hostFirstName,
                    lastName: eventOccurrence.hostLastName,
                    picture: eventOccurrence.hostPicture,
                  }}
                />
                <div className="flex flex-row items-center gap-2">
                  <CalendarIcon
                    pathClassName="stroke-gray-500"
                    className="size-5"
                  />
                  <div className="dark:text-gray-0 line-clamp-1 text-sm font-semibold text-gray-950">
                    {DateTime.fromISO(eventOccurrence.startAt)
                      .setZone(club.timezone)
                      .toLocaleString({
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                  </div>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <ClockIcon
                    pathClassName="stroke-gray-500"
                    className="size-5"
                  />
                  <div className="dark:text-gray-0 line-clamp-1 text-sm font-semibold text-gray-950">
                    {Interval.fromDateTimes(
                      DateTime.fromISO(eventOccurrence.startAt).setZone(
                        club.timezone,
                      ),
                      DateTime.fromISO(eventOccurrence.endAt).setZone(
                        club.timezone,
                      ),
                    ).toLocaleString(DateTime.TIME_SIMPLE)}
                  </div>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <LocationMarkerIcon
                    pathClassName="stroke-gray-500"
                    className="size-5"
                  />
                  <div className="dark:text-gray-0 line-clamp-1 text-sm font-semibold text-gray-950">
                    {eventOccurrence.facilityName}
                  </div>
                </div>
              </div>
            </div>
            {eventOccurrence?.rsvpStatus &&
              (["BOOKED", "WAITING"] as EventRsvpStatus[]).includes(
                eventOccurrence?.rsvpStatus,
              ) && (
                <>
                  {isCancelWithoutFee && (
                    <Button
                      className="w-full"
                      intent="danger"
                      onClick={async () => {
                        await mutateAsyncCancelRsvp({
                          rsvpId: eventOccurrence.rsvpId,
                        });

                        toast({
                          message: t(
                            "pages.hostedPagesClassesOverview.classCancelled",
                          ),
                          type: "small",
                        });
                        history.push(
                          createSiteOrEmbedLink(
                            `${RouteFeature.SiteTimetableType.replace(
                              ":viewType",
                              viewType,
                            )}?${getTimetableQueryParams()}`,
                          ),
                        );
                      }}
                    >
                      {isLeaveWaitlistAvailable
                        ? t("pages.hostedPagesClassesOverview.leaveWaitlist")
                        : t("pages.hostedPagesClassesOverview.cancelBooking")}
                    </Button>
                  )}
                  {!isCancelWithoutFee && (
                    <>
                      <div className="flex w-full flex-col gap-2">
                        <div className="flex flex-row items-center gap-2">
                          <LayeredCircles intent="warning" className="size-8">
                            <ExclamationCircleIcon pathClassName="stroke-warning-600" />
                          </LayeredCircles>
                          <div className="text-warning-600 font-semibold">
                            {t(
                              "pages.hostedPagesClassesOverview.lateCancelationFeeTitle",
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {lateCancelationFee
                            ? t(
                                "pages.hostedPagesClassesOverview.lateCancelationFeeDescription",
                                {
                                  fee: formatCurrency(
                                    lateCancelationFee,
                                    club.defaultCurrency,
                                  ),
                                },
                              )
                            : t(
                                "pages.hostedPagesClassesOverview.lateCancelationFeeDescriptionNoFee",
                              )}
                        </div>
                      </div>
                      <Button
                        className="w-full"
                        intent="danger"
                        {...(lateCancelationFee
                          ? {
                              link: createSiteOrEmbedLink(
                                RouteFeature.SiteTimeTableLateCancelId.replace(
                                  ":viewType",
                                  viewType,
                                ).replace(
                                  ":eventOccurrenceId",
                                  eventOccurrenceId,
                                ),
                              ),
                            }
                          : {
                              onClick: async () => {
                                await mutateAsyncLateCancel({
                                  rsvpId: eventOccurrence.rsvpId,
                                });

                                toast({
                                  message: t(
                                    "pages.hostedPagesClassesOverview.classCancelled",
                                  ),
                                  type: "small",
                                });
                                history.push(
                                  createSiteOrEmbedLink(
                                    `${RouteFeature.SiteTimetableType.replace(
                                      ":viewType",
                                      viewType,
                                    )}?${getTimetableQueryParams()}`,
                                  ),
                                );
                              },
                            })}
                      >
                        {!lateCancelationFee
                          ? t("pages.hostedPagesClassesOverview.cancelBooking")
                          : t(
                              "pages.hostedPagesClassesOverview.cancelBookingAndPay",
                            )}
                      </Button>
                    </>
                  )}
                </>
              )}
            {(!eventOccurrence?.rsvpStatus ||
              (["LATE_CANCELLED", "CANCELLED"] as EventRsvpStatus[]).includes(
                eventOccurrence?.rsvpStatus,
              )) && (
              <>
                <div className="flex w-full border-b border-gray-300 dark:border-gray-700" />
                <div className="flex w-full flex-col gap-4">
                  <div className="flex w-full flex-col gap-2">
                    <div className="flex flex-row items-center gap-2">
                      <LayeredCircles className="size-8" intent="default">
                        <ExclamationCircleIcon pathClassName="stroke-gray-500" />
                      </LayeredCircles>
                      <div className="dark:text-gray-0 font-semibold text-gray-950">
                        {t("page.hostedPagesClasses.credit")}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {t("page.hostedPagesClasses.creditDescription")}
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    {sessionPackUsage &&
                      sessionPackUsage?.result !== "NOT_ENOUGH_SESSIONS" &&
                      sessionPackUsage?.sessionPackUsageCalculations
                        .filter((e) => e.numberOfSessionsUsed > 0)
                        .map((usageEntry, i) => (
                          <div
                            key={i}
                            className="flex flex-row items-center justify-between rounded-xl border border-gray-300 p-3 dark:border-gray-700 dark:bg-gray-900"
                          >
                            <div className="flex flex-col">
                              <div className="dark:text-gray-0 text-sm font-bold text-gray-950">
                                {usageEntry.sessionPack.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {t("page.hostedPagesClasses.creditExpiry", {
                                  date: usageEntry.sessionPack.endDate
                                    ? DateTime.fromISO(
                                        usageEntry.sessionPack.endDate,
                                      )
                                        .setZone(club.timezone)
                                        .toLocaleString(DateTime.DATE_FULL)
                                    : t("portal.common.unlimited"),
                                })}
                              </div>
                            </div>
                            <div className="flex flex-row gap-2">
                              <div className="dark:text-gray-0 text-xl font-bold text-gray-950">
                                {usageEntry.numberOfSessionsUsed}
                              </div>
                              <CreditsIcon className="stroke-secondary-500 size-6" />
                            </div>
                          </div>
                        ))}
                  </div>
                  {sessionPackUsage &&
                    sessionPackUsage?.result === "NOT_ENOUGH_SESSIONS" && (
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-row items-center justify-between rounded-xl border border-gray-300 p-3 dark:border-gray-700 dark:bg-gray-900">
                          <div className="flex flex-col">
                            <div className="dark:text-gray-0 text-sm font-bold text-gray-950">
                              {t("page.hostedPagesClasses.noCreditPack")}
                            </div>
                          </div>
                          <div className="flex flex-row gap-2">
                            <div className="dark:text-gray-0 text-xl font-bold text-gray-950">
                              0
                            </div>
                            <CreditsIcon className="stroke-secondary-500 size-6" />
                          </div>
                        </div>

                        <Button
                          link={createSiteOrEmbedLink(
                            RouteFeature.SiteTimeTableQuickPurchase.replace(
                              ":viewType",
                              viewType,
                            )
                              .replace(":eventOccurrenceId", eventOccurrenceId)
                              .replace(":type", "session-pack"),
                          )}
                          className="h-12 w-full justify-between rounded-lg border-gray-300 py-3 dark:border-gray-700"
                        >
                          <div className="text-md dark:text-gray-0 flex flex-row items-center gap-2 font-medium text-gray-950">
                            <ShopBagIcon pathClassName="stroke-gray-500" />
                            {t("page.hostedPagesClasses.buyCreditPack")}
                          </div>
                          <FontAwesomeIcon
                            className="text-gray-500"
                            icon={faAngleRight}
                          />
                        </Button>
                      </div>
                    )}
                </div>

                <div className="flex w-full border-b-2 border-dashed border-gray-300 dark:border-gray-700" />
                <div className="flex w-full flex-col gap-2">
                  <div className="flex w-full flex-row items-center justify-between">
                    <div className="text-gray-500">
                      {eventOccurrence.activityName}
                    </div>
                    <div className="text-gray-500">
                      {pluralize(
                        t("page.hostedPagesClasses.creditCostAmount", {
                          amount: eventOccurrence.sessionCost,
                        }),
                        t("page.hostedPagesClasses.creditCostAmounts", {
                          amount: eventOccurrence.sessionCost,
                        }),
                        eventOccurrence.sessionCost,
                      )}
                    </div>
                  </div>
                  <div className="dark:text-gray-0 flex w-full flex-row items-center justify-between text-gray-950">
                    <div className="text-xl font-bold">Amount Due</div>
                    <div className="text-xl font-bold">
                      {pluralize(
                        t("page.hostedPagesClasses.creditCostAmount", {
                          amount: eventOccurrence.sessionCost,
                        }),
                        t("page.hostedPagesClasses.creditCostAmounts", {
                          amount: eventOccurrence.sessionCost,
                        }),
                        eventOccurrence.sessionCost,
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-1">
                  {slotsAvailable > 0 && slotsAvailable < 3 && (
                    <div className="text-xm text-warning-500 font-semibold">
                      {pluralize(
                        t("page.hostedPagesClasses.amountSpotLeft", {
                          amount: slotsAvailable,
                        }),
                        t("page.hostedPagesClasses.amountSpotsLeft", {
                          amount: slotsAvailable,
                        }),
                        slotsAvailable,
                      )}
                    </div>
                  )}
                  {slotsAvailable === 0 && eventOccurrence.waitingCount > 0 && (
                    <div className="text-xm font-semibold text-gray-500">
                      {t("page.hostedPagesClasses.amountWaiting", {
                        amount: eventOccurrence.waitingCount,
                      })}
                    </div>
                  )}
                  {!isEventInsideBookingWindow && (
                    <div className="text-warning-500 text-sm font-semibold">
                      {t("addPaymentMethodModal.outsideBookingWindow")}
                    </div>
                  )}
                  <Button
                    disabled={
                      !isEventInsideBookingWindow ||
                      !sessionPackUsage ||
                      sessionPackUsage.result === "NOT_ENOUGH_SESSIONS"
                    }
                    intent="secondary"
                    className="w-fit px-16"
                    onClick={async () => {
                      if (!id) return;

                      if (eventOccurrence?.rsvpId) {
                        await mutateAsyncBookRsvp({
                          rsvpId: eventOccurrence.rsvpId,
                        });
                      } else {
                        await mutateAsyncAddMemberToOccurrence({
                          occurrenceId: eventOccurrence.occurrenceId,
                        });
                      }

                      toast({
                        intent: "success",
                        type: "small",
                        message: t(
                          "pages.hostedPagesClassesOverview.bookingSuccess",
                        ),
                      });
                      history.push(
                        createSiteOrEmbedLink(
                          `${RouteFeature.SiteTimetableType.replace(
                            ":viewType",
                            viewType,
                          )}?${getTimetableQueryParams()}`,
                        ),
                      );
                    }}
                  >
                    {bookActionType === "BOOK"
                      ? pluralize(
                          t(
                            "pages.hostedPagesClassesOverview.bookForCreditAmount",
                            {
                              amount: eventOccurrence.sessionCost,
                            },
                          ),
                          t(
                            "pages.hostedPagesClassesOverview.bookForCreditsAmount",
                            {
                              amount: eventOccurrence.sessionCost,
                            },
                          ),
                          eventOccurrence.sessionCost,
                        )
                      : bookActionType === "JOIN_WAITLIST"
                        ? t("pages.hostedPagesClassesOverview.joinWaitlist", {
                            amount: eventOccurrence.sessionCost,
                          })
                        : t("pages.hostedPagesClassesOverview.classFull")}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <HostedPagesQuickPurchaseOverlay
        closeOverlay={closeOverlay}
        purchaseFinishedButton={
          <Button
            link={createSiteOrEmbedLink(
              RouteFeature.SiteTimetableId.replace(
                ":viewType",
                viewType,
              ).replace(":eventOccurrenceId", eventOccurrenceId!),
            )}
          >
            {t("pages.hostedPagesCheckout.proceedToBooking")}
          </Button>
        }
      />
    </div>
  );
};
